import { MoreHorizontal, PhoneOff, Plus, Unlock } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Page, PageHeader } from "@/components/layout/page";
import { BlockCallerDialog, UnblockConfirm, useEndBlock } from "@/features/block-caller";
import { useAuth } from "@/lib/auth";
import { callerLabel, canUnblock, untilText, type BlockedCaller } from "@/lib/blocked-callers";
import { useBlockedCallers } from "@/lib/blocked-callers-api";
import { relativeTime } from "@/lib/utils";

/**
 * Blocked callers (ADR-159 in platform): the callers the switch turns away before any phone rings,
 * on every number we own. A blocked number hears busy; hidden callers hear a short message.
 *
 * Reps block from the phone for up to a day. Here a supervisor sees every block in force, blocks a
 * number for good, and ends a block early. A block that has run out leaves the list by itself.
 */
export function BlockedCallersPage() {
  const { user } = useAuth();
  const { data, isPending, error, refetch } = useBlockedCallers();
  const unblock = useEndBlock();
  const [adding, setAdding] = React.useState(false);
  const [ending, setEnding] = React.useState<BlockedCaller | null>(null);
  const mayUnblock = canUnblock(user?.role);

  const addButton = (
    <Button variant="default" onClick={() => setAdding(true)}>
      <Plus />
      Block a caller
    </Button>
  );

  return (
    <Page>
      <PageHeader
        title="Blocked callers"
        description="Callers the phone system turns away before any phone rings, on every number we own. A blocked number hears a busy tone; hidden callers hear that we do not accept anonymous calls. Reps block from the phone for up to a day."
        action={addButton}
      />
      <Card className="overflow-hidden">
        {isPending ? (
          <TableSkeleton cols={5} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load blocked callers"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={PhoneOff}
            title="Nobody is blocked"
            description="A caller blocked from the phone, a contact or this page shows here until the block ends."
            action={addButton}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Caller</TableHead>
                <TableHead>Blocked</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Why</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((b) => (
                <TableRow key={b.id} className="[&>td]:h-11">
                  <TableCell>
                    <span className="inline-flex items-center gap-2">
                      {b.hidden ? (
                        <Badge tone="neutral">Hidden callers</Badge>
                      ) : (
                        <span className="font-mono text-text">{callerLabel(b)}</span>
                      )}
                      {b.lead_id && (
                        <Link
                          to={`/contacts/${b.lead_id}`}
                          className="text-xs font-medium text-accent-11 hover:underline"
                        >
                          Contact
                        </Link>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={b.expires_at === null ? "font-medium text-danger-11" : "text-text"}>
                      {untilText(b)}
                    </span>
                  </TableCell>
                  <TableCell className="text-text-muted">
                    <span title={b.created_at}>
                      {b.blocked_by ?? "—"} · {relativeTime(b.created_at)}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-72 truncate text-text-muted" title={b.note ?? undefined}>
                    {b.note ?? "—"}
                  </TableCell>
                  <TableCell>
                    {mayUnblock && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${callerLabel(b)}`}>
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onSelect={() => setEnding(b)}>
                            <Unlock />
                            Unblock
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <BlockCallerDialog open={adding} onOpenChange={setAdding} target={null} />

      <UnblockConfirm
        block={ending}
        onOpenChange={(open) => !open && setEnding(null)}
        loading={unblock.pending}
        onConfirm={async (block) => {
          if (await unblock.end(block)) setEnding(null);
        }}
      />
    </Page>
  );
}
